import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { Plus, Edit, Trash2, Loader2, ArrowLeft, Star } from 'lucide-react';
import { programsAPI, getImageUrl } from '../services/api';
import { useToast } from '../hooks/use-toast';
import { useLanguage } from '../context/LanguageContext';
import { translations } from '../translations/translations';
import RichTextEditor from '../components/RichTextEditor';
import DraggablePrizes from '../components/DraggablePrizes';

const AdminPrograms = () => {
  const [programs, setPrograms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingProgram, setEditingProgram] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const { toast } = useToast();
  const { language } = useLanguage();
  const t = translations[language];

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  const [formData, setFormData] = useState({
    name: '',
    organization: '',
    description: '',
    challenge: '',
    prize: '',
    prizes: [],
    deadline: '',
    status: 'draft',
    category: 'Technology',
    location: '',
    image: '',
    technicalBasesUrl: '',
    technicalBasesFilename: '',
    stages: ['Application', 'Screening', 'Finals'],
    applicationSteps: [],
    eligibilityRequirements: [],
    customSections: [],
    verticals: [],
    scoringCriteria: []
  });
  const [docFile, setDocFile] = useState(null);
  const [uploadingDoc, setUploadingDoc] = useState(false);

  useEffect(() => {
    fetchPrograms();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchPrograms = async () => {
    try {
      setLoading(true);
      const data = await programsAPI.getAll({ include_drafts: true });
      setPrograms(data);
    } catch (error) {
      console.error('Error fetching programs:', error);
      toast({
        title: 'Error',
        description: 'Failed to load programs',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const resetForm = () => {
    setFormData({
      name: '',
      organization: '',
      description: '',
      challenge: '',
      prize: '',
      prizes: [],
      deadline: '',
      status: 'draft',
      category: 'Technology',
      location: '',
      image: '',
      technicalBasesUrl: '',
      technicalBasesFilename: '',
      stages: ['Application', 'Screening', 'Finals'],
      applicationSteps: [],
      eligibilityRequirements: [],
      customSections: []
    });
    setEditingProgram(null);
    setImageFile(null);
    setImagePreview(null);
    setDocFile(null);
  };

  const handleEdit = (program) => {
    setEditingProgram(program);
    setFormData({
      name: program.name,
      organization: program.organization,
      description: program.description,
      challenge: program.challenge || '',
      prize: program.prize || '',
      prizes: program.prizes || [],
      deadline: program.deadline.split('T')[0],
      status: program.status,
      category: program.category,
      location: program.location,
      image: program.image,
      technicalBasesUrl: program.technicalBasesUrl || '',
      technicalBasesFilename: program.technicalBasesFilename || '',
      stages: program.stages,
      applicationSteps: program.applicationSteps || [],
      eligibilityRequirements: program.eligibilityRequirements || [],
      customSections: program.customSections || [],
      verticals: program.verticals || [],
      scoringCriteria: program.scoringCriteria || []
    });
    setImagePreview(getImageUrl(program.image));
    setImageFile(null);
    setDocFile(null);
    setIsDialogOpen(true);
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 10 * 1024 * 1024) {
        toast({
          title: 'Error',
          description: language === 'es' ? 'La imagen debe ser menor a 10MB' : 'Image must be less than 10MB',
          variant: 'destructive'
        });
        return;
      }
      setImageFile(file);
      setImagePreview(URL.createObjectURL(file));
    }
  };

  const uploadImage = async () => {
    if (!imageFile) return formData.image;

    try {
      setUploading(true);
      const formDataObj = new FormData();
      formDataObj.append('file', imageFile);

      const response = await fetch(`${API_URL}/api/uploads/program-image`, {
        method: 'POST',
        body: formDataObj
      });

      if (!response.ok) throw new Error('Upload failed');

      const data = await response.json();
      // Return the relative path - it already includes /api/uploads prefix
      return data.primary_url;
    } catch (error) {
      console.error('Error uploading image:', error);
      throw error;
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      setSubmitting(true);
      
      // Upload image if a new file was selected
      let imageUrl = formData.image;
      if (imageFile) {
        imageUrl = await uploadImage();
      }

      // Upload document if selected
      let docUrl = formData.technicalBasesUrl;
      let docFilename = formData.technicalBasesFilename;
      if (docFile) {
        setUploadingDoc(true);
        const docFormData = new FormData();
        docFormData.append('file', docFile);
        const docResponse = await fetch(`${API_URL}/api/uploads/document`, {
          method: 'POST',
          body: docFormData
        });
        if (docResponse.ok) {
          const docData = await docResponse.json();
          docUrl = docData.url;
          docFilename = docData.filename;
        }
        setUploadingDoc(false);
      }

      const programData = { ...formData, image: imageUrl, technicalBasesUrl: docUrl, technicalBasesFilename: docFilename };
      
      if (editingProgram) {
        // Update existing program
        await programsAPI.update(editingProgram._id, programData);
        toast({
          title: language === 'es' ? 'Desafío Actualizado' : 'Challenge Updated',
          description: language === 'es' ? 'El desafío ha sido actualizado exitosamente' : 'The challenge has been updated successfully'
        });
      } else {
        // Create new program
        await programsAPI.create(programData);
        toast({
          title: language === 'es' ? 'Desafío Creado' : 'Challenge Created',
          description: language === 'es' ? 'El desafío ha sido creado exitosamente' : 'The challenge has been created successfully'
        });
      }
      
      setIsDialogOpen(false);
      resetForm();
      fetchPrograms();
    } catch (error) {
      console.error('Error saving program:', error);
      toast({
        title: 'Error',
        description: language === 'es' ? 'Error al guardar el desafío' : 'Failed to save challenge',
        variant: 'destructive'
      });
    } finally {
      setSubmitting(false);
    }
  };

  // Prizes handlers
  const addPrize = () => {
    setFormData(prev => ({
      ...prev,
      prizes: [...prev.prizes, { place: '', amount: '', description: '' }]
    }));
  };

  const updatePrize = (index, field, value) => {
    setFormData(prev => {
      const newPrizes = [...prev.prizes];
      newPrizes[index] = { ...newPrizes[index], [field]: value };
      return { ...prev, prizes: newPrizes };
    });
  };

  const removePrize = (index) => {
    setFormData(prev => ({
      ...prev,
      prizes: prev.prizes.filter((_, i) => i !== index)
    }));
  };

  const handleDocChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const ext = file.name.split('.').pop().toLowerCase();
      if (!['pdf', 'doc', 'docx'].includes(ext)) {
        toast({
          title: 'Error',
          description: language === 'es' ? 'Solo se permiten archivos PDF, DOC o DOCX' : 'Only PDF, DOC or DOCX files are allowed',
          variant: 'destructive'
        });
        return;
      }
      if (file.size > 20 * 1024 * 1024) {
        toast({
          title: 'Error',
          description: language === 'es' ? 'El archivo debe ser menor a 20MB' : 'File must be less than 20MB',
          variant: 'destructive'
        });
        return;
      }
      setDocFile(file);
    }
  };

  const handleDelete = async (programId) => {
    if (!window.confirm(language === 'es' ? '¿Estás seguro de que deseas eliminar este desafío?' : 'Are you sure you want to delete this challenge?')) {
      return;
    }

    try {
      await programsAPI.delete(programId);
      toast({
        title: language === 'es' ? 'Desafío Eliminado' : 'Challenge Deleted',
        description: language === 'es' ? 'El desafío ha sido eliminado' : 'The challenge has been deleted'
      });
      fetchPrograms();
    } catch (error) {
      console.error('Error deleting program:', error);
      toast({
        title: 'Error',
        description: language === 'es' ? 'Error al eliminar el desafío' : 'Failed to delete challenge',
        variant: 'destructive'
      });
    }
  };

  const handleToggleFeatured = async (programId) => {
    try {
      const result = await programsAPI.toggleFeatured(programId);
      toast({
        title: result.featured
          ? (language === 'es' ? 'Desafio Destacado' : 'Challenge Featured')
          : (language === 'es' ? 'Destaque Removido' : 'Feature Removed'),
        description: result.featured
          ? (language === 'es' ? 'El desafio ahora aparece como destacado' : 'The challenge is now featured')
          : (language === 'es' ? 'El desafio ya no esta destacado' : 'The challenge is no longer featured')
      });
      fetchPrograms();
    } catch (error) {
      console.error('Error toggling featured:', error);
      toast({
        title: 'Error',
        description: language === 'es' ? 'Error al cambiar destaque' : 'Failed to toggle featured',
        variant: 'destructive'
      });
    }
  };

  return (
    <div className="min-h-screen bg-sand-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div>
              <Button asChild variant="ghost" className="mb-4">
                <Link to="/dashboard">
                  <ArrowLeft className="mr-2 w-4 h-4" /> {language === 'es' ? 'Volver al Panel' : 'Back to Dashboard'}
                </Link>
              </Button>
              <h1 className="text-3xl font-bold text-black">
                {language === 'es' ? 'Administrar Desafíos' : 'Manage Challenges'}
              </h1>
              <p className="text-gray-600 mt-1">
                {language === 'es' ? 'Crear, editar y publicar desafíos' : 'Create, edit, and publish challenges'}
              </p>
            </div>
            <Dialog open={isDialogOpen} onOpenChange={(open) => {
              setIsDialogOpen(open);
              if (!open) resetForm();
            }}>
              <DialogTrigger asChild>
                <Button className="bg-secondary text-white hover:bg-secondary/90">
                  <Plus className="mr-2 w-4 h-4" /> {language === 'es' ? 'Nuevo Desafío' : 'New Challenge'}
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>
                    {editingProgram
                      ? (language === 'es' ? 'Editar Desafío' : 'Edit Challenge')
                      : (language === 'es' ? 'Crear Nuevo Desafío' : 'Create New Challenge')}
                  </DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="name">{language === 'es' ? 'Nombre del Desafío' : 'Challenge Name'} *</Label>
                    <Input
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="organization">{language === 'es' ? 'Organización' : 'Organization'} *</Label>
                    <Input
                      id="organization"
                      name="organization"
                      value={formData.organization}
                      onChange={handleInputChange}
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="description">{language === 'es' ? 'Descripción' : 'Description'} *</Label>
                    <Textarea
                      id="description"
                      name="description"
                      value={formData.description}
                      onChange={handleInputChange}
                      className="min-h-24"
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="challenge">{language === 'es' ? 'El Desafio' : 'The Challenge'}</Label>
                    <p className="text-xs text-gray-500">{language === 'es' ? 'Describe el problema o desafio especifico que se busca resolver' : 'Describe the specific problem or challenge to solve'}</p>
                    <RichTextEditor
                      value={formData.challenge}
                      onChange={(val) => setFormData(prev => ({ ...prev, challenge: val }))}
                      placeholder={language === 'es' ? 'Ej: Buscamos soluciones tecnológicas para reducir la brecha digital en zonas rurales...' : 'e.g. We seek technology solutions to reduce the digital gap in rural areas...'}
                    />
                  </div>

                  {/* Flexible Prizes Section */}
                  <DraggablePrizes
                    prizes={formData.prizes}
                    onChange={(newPrizes) => setFormData(prev => ({ ...prev, prizes: newPrizes }))}
                    language={language}
                  />

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="deadline">{language === 'es' ? 'Fecha Limite' : 'Deadline'} *</Label>
                      <Input
                        id="deadline"
                        name="deadline"
                        type="date"
                        value={formData.deadline}
                        onChange={handleInputChange}
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>{language === 'es' ? 'Bases Tecnicas (PDF/Word)' : 'Technical Bases (PDF/Word)'}</Label>
                      <Input
                        type="file"
                        accept=".pdf,.doc,.docx"
                        onChange={handleDocChange}
                        className="border-2 border-gray-200"
                      />
                      {(formData.technicalBasesFilename || docFile) && (
                        <p className="text-xs text-green-600">
                          {docFile ? docFile.name : formData.technicalBasesFilename}
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="category">{language === 'es' ? 'Categoría' : 'Category'} *</Label>
                      <Select
                        value={formData.category}
                        onValueChange={(value) => handleSelectChange('category', value)}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Technology">{language === 'es' ? 'Tecnología' : 'Technology'}</SelectItem>
                          <SelectItem value="Healthcare">{language === 'es' ? 'Salud' : 'Healthcare'}</SelectItem>
                          <SelectItem value="Sustainability">{language === 'es' ? 'Sustentabilidad' : 'Sustainability'}</SelectItem>
                          <SelectItem value="Education">{language === 'es' ? 'Educación' : 'Education'}</SelectItem>
                          <SelectItem value="Finance">{language === 'es' ? 'Finanzas' : 'Finance'}</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="status">{language === 'es' ? 'Estado' : 'Status'} *</Label>
                      <Select
                        value={formData.status}
                        onValueChange={(value) => handleSelectChange('status', value)}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="draft">{language === 'es' ? 'Borrador' : 'Draft'}</SelectItem>
                          <SelectItem value="open">{language === 'es' ? 'Abierto' : 'Open'}</SelectItem>
                          <SelectItem value="closed">{language === 'es' ? 'Cerrado' : 'Closed'}</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  {/* Verticals / Sub-categories */}
                  <div className="space-y-2">
                    <Label>
                      {language === 'es' ? 'Verticales' : 'Verticals'}
                    </Label>
                    <p className="text-xs text-gray-500">
                      {language === 'es' 
                        ? 'Agrega sub-categorías específicas. Ej: Fintech, Cripto, Insurtech, etc.'
                        : 'Add specific sub-categories. E.g.: Fintech, Crypto, Insurtech, etc.'}
                    </p>
                    <div className="flex flex-wrap gap-2 mb-2">
                      {(formData.verticals || []).map((v, i) => (
                        <Badge key={i} variant="secondary" className="flex items-center gap-1 px-3 py-1">
                          {v}
                          <button
                            type="button"
                            className="ml-1 hover:text-red-600"
                            onClick={() => setFormData(prev => ({
                              ...prev,
                              verticals: prev.verticals.filter((_, idx) => idx !== i)
                            }))}
                          >
                            ×
                          </button>
                        </Badge>
                      ))}
                    </div>
                    <div className="flex gap-2">
                      <Input
                        id="vertical-input"
                        placeholder={language === 'es' ? 'Ej: Fintech' : 'E.g.: Fintech'}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            const val = e.target.value.trim();
                            if (val && !(formData.verticals || []).includes(val)) {
                              setFormData(prev => ({
                                ...prev,
                                verticals: [...(prev.verticals || []), val]
                              }));
                              e.target.value = '';
                            }
                          }
                        }}
                        className="flex-1"
                      />
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          const input = document.getElementById('vertical-input');
                          const val = input?.value?.trim();
                          if (val && !(formData.verticals || []).includes(val)) {
                            setFormData(prev => ({
                              ...prev,
                              verticals: [...(prev.verticals || []), val]
                            }));
                            input.value = '';
                          }
                        }}
                      >
                        <Plus className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="location">{language === 'es' ? 'Ubicación' : 'Location'} *</Label>
                    <Input
                      id="location"
                      name="location"
                      value={formData.location}
                      onChange={handleInputChange}
                      placeholder={language === 'es' ? 'Global, Chile, etc.' : 'Global, Chile, etc.'}
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="image">{language === 'es' ? 'Imagen del Desafío' : 'Challenge Image'} *</Label>
                    <div className="space-y-4">
                      {imagePreview && (
                        <div className="relative">
                          <img
                            src={imagePreview}
                            alt="Preview"
                            className="w-full h-40 object-cover rounded border-2 border-gray-200"
                          />
                          <Button
                            type="button"
                            size="sm"
                            variant="ghost"
                            onClick={() => {
                              setImageFile(null);
                              setImagePreview(null);
                              setFormData(prev => ({ ...prev, image: '' }));
                            }}
                            className="absolute top-2 right-2 bg-white/90 hover:bg-white text-red-600"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      )}
                      <Input
                        id="image"
                        type="file"
                        accept="image/*"
                        onChange={handleImageChange}
                        className="border-2 border-gray-200"
                      />
                      <p className="text-xs text-gray-600">
                        {language === 'es' 
                          ? 'Sube una imagen (máx 10MB). Se redimensionará automáticamente.' 
                          : 'Upload an image (max 10MB). Will be auto-resized.'}
                      </p>
                    </div>
                  </div>

                  {/* Custom Sections */}
                  <div className="space-y-4 pt-4 border-t border-gray-300">
                    <div className="flex justify-between items-center">
                      <div>
                        <Label className="text-lg font-semibold">
                          {language === 'es' ? 'Secciones del Desafío' : 'Challenge Sections'}
                        </Label>
                        <p className="text-sm text-gray-500 mt-1">
                          {language === 'es' 
                            ? 'Agrega las secciones que necesites. Ej: "Elegibilidad", "Proceso de Postulación", "Beneficios", "Criterios de Evaluación"'
                            : 'Add the sections you need. E.g.: "Eligibility", "Application Process", "Benefits", "Evaluation Criteria"'}
                        </p>
                      </div>
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        onClick={() => setFormData(prev => ({
                          ...prev,
                          customSections: [...(prev.customSections || []), { title: '', items: [] }]
                        }))}
                        className="border-2 shrink-0"
                      >
                        <Plus className="w-4 h-4 mr-1" /> {language === 'es' ? 'Agregar Sección' : 'Add Section'}
                      </Button>
                    </div>
                    
                    {(formData.customSections || []).map((section, sectionIndex) => (
                      <div key={sectionIndex} className="border-2 border-gray-200 rounded-lg p-4 space-y-3">
                        <div className="flex gap-2 items-center">
                          <Input
                            placeholder={language === 'es' ? 'Título de la sección (ej: Elegibilidad)' : 'Section title (e.g.: Eligibility)'}
                            value={section.title}
                            onChange={(e) => {
                              const newSections = [...formData.customSections];
                              newSections[sectionIndex].title = e.target.value;
                              setFormData(prev => ({ ...prev, customSections: newSections }));
                            }}
                            className="flex-1 font-semibold"
                          />
                          <Button
                            type="button"
                            size="sm"
                            variant="ghost"
                            onClick={() => {
                              const newSections = formData.customSections.filter((_, i) => i !== sectionIndex);
                              setFormData(prev => ({ ...prev, customSections: newSections }));
                            }}
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                        
                        <div className="space-y-2 pl-4">
                          {section.items.map((item, itemIndex) => (
                            <div key={itemIndex} className="flex gap-2 items-center">
                              <span className="flex-shrink-0 w-2 h-2 rounded-full bg-gray-400"></span>
                              <Input
                                placeholder={language === 'es' ? 'Escribe el contenido del item...' : 'Write item content...'}
                                value={item.text}
                                onChange={(e) => {
                                  const newSections = [...formData.customSections];
                                  newSections[sectionIndex].items[itemIndex].text = e.target.value;
                                  setFormData(prev => ({ ...prev, customSections: newSections }));
                                }}
                                className="flex-1"
                              />
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                onClick={() => {
                                  const newSections = [...formData.customSections];
                                  newSections[sectionIndex].items = section.items.filter((_, i) => i !== itemIndex);
                                  setFormData(prev => ({ ...prev, customSections: newSections }));
                                }}
                                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          ))}
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="text-gray-600 hover:text-gray-800"
                            onClick={() => {
                              const newSections = [...formData.customSections];
                              newSections[sectionIndex].items = [...section.items, { text: '' }];
                              setFormData(prev => ({ ...prev, customSections: newSections }));
                            }}
                          >
                            <Plus className="w-4 h-4 mr-1" />
                            {language === 'es' ? 'Agregar Item' : 'Add Item'}
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Scoring Criteria Section */}
                  <div className="space-y-4 pt-4 border-t border-gray-300">
                    <div className="flex justify-between items-center">
                      <div>
                        <Label className="text-lg font-semibold">
                          {language === 'es' ? 'Criterios de Evaluación' : 'Evaluation Criteria'}
                        </Label>
                        <p className="text-sm text-gray-500 mt-1">
                          {language === 'es'
                            ? 'Define los criterios con los que los evaluadores calificarán (escala 1-10).'
                            : 'Define the criteria evaluators will use to score (1-10 scale).'}
                        </p>
                      </div>
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        onClick={() => setFormData(prev => ({
                          ...prev,
                          scoringCriteria: [...(prev.scoringCriteria || []), { name: '', description: '', maxScore: 10, weight: 1.0 }]
                        }))}
                        className="border-2 shrink-0"
                      >
                        <Plus className="w-4 h-4 mr-1" /> {language === 'es' ? 'Agregar Criterio' : 'Add Criterion'}
                      </Button>
                    </div>

                    {(formData.scoringCriteria || []).length === 0 && (
                      <div className="text-center py-4 bg-gray-50 rounded-lg border border-dashed border-gray-300">
                        <p className="text-sm text-gray-500">
                          {language === 'es'
                            ? 'Sin criterios definidos. Se usarán criterios por defecto.'
                            : 'No criteria defined. Default criteria will be used.'}
                        </p>
                      </div>
                    )}

                    {(formData.scoringCriteria || []).map((criterion, index) => (
                      <div key={index} className="border-2 border-gray-200 rounded-lg p-4">
                        <div className="flex gap-2 items-start">
                          <div className="flex-1 grid grid-cols-2 gap-3">
                            <Input
                              placeholder={language === 'es' ? 'Ej: Innovación' : 'E.g.: Innovation'}
                              value={criterion.name}
                              onChange={(e) => {
                                const c = [...formData.scoringCriteria];
                                c[index].name = e.target.value;
                                setFormData(prev => ({ ...prev, scoringCriteria: c }));
                              }}
                              className="font-semibold"
                            />
                            <Input
                              placeholder={language === 'es' ? 'Descripción del criterio' : 'Criterion description'}
                              value={criterion.description}
                              onChange={(e) => {
                                const c = [...formData.scoringCriteria];
                                c[index].description = e.target.value;
                                setFormData(prev => ({ ...prev, scoringCriteria: c }));
                              }}
                            />
                          </div>
                          <Button
                            type="button"
                            size="sm"
                            variant="ghost"
                            onClick={() => setFormData(prev => ({
                              ...prev,
                              scoringCriteria: prev.scoringCriteria.filter((_, i) => i !== index)
                            }))}
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="flex gap-4 pt-4">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => {
                        setIsDialogOpen(false);
                        resetForm();
                      }}
                      className="flex-1"
                    >
                      {language === 'es' ? 'Cancelar' : 'Cancel'}
                    </Button>
                    <Button
                      type="submit"
                      disabled={submitting || uploading}
                      className="flex-1 bg-secondary text-white hover:bg-secondary/90"
                    >
                      {uploading ? (
                        <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> {language === 'es' ? 'Subiendo imagen...' : 'Uploading image...'}</>
                      ) : submitting ? (
                        <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> {language === 'es' ? 'Guardando...' : 'Saving...'}</>
                      ) : (
                        editingProgram ? (language === 'es' ? 'Actualizar' : 'Update') : (language === 'es' ? 'Crear' : 'Create')
                      )}
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </div>
      </div>

      {/* Programs List */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {loading ? (
          <div className="flex justify-center items-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-6">
            {programs.map((program) => (
              <Card key={program._id} className="border-2 border-gray-200 hover:border-primary transition-all">
                <CardContent className="p-6">
                  <div className="flex items-start gap-6">
                    <img
                      src={getImageUrl(program.image)}
                      alt={program.name}
                      className="w-32 h-32 object-cover rounded"
                    />
                    <div className="flex-1">
                      <div className="flex items-start justify-between">
                        <div>
                          <h3 className="text-2xl font-bold text-black mb-2">{program.name}</h3>
                          <p className="text-gray-600 mb-2">{program.organization}</p>
                          <div className="flex gap-2 mb-4">
                            <Badge className={program.status === 'open' ? 'bg-primary text-white' : program.status === 'draft' ? 'bg-gray-400' : 'bg-gray-300'}>
                              {program.status === 'open' ? (language === 'es' ? 'Abierto' : 'Open') : program.status === 'draft' ? (language === 'es' ? 'Borrador' : 'Draft') : (language === 'es' ? 'Cerrado' : 'Closed')}
                            </Badge>
                            {program.featured && (
                              <Badge className="bg-orange-400 text-white">
                                <Star className="w-3 h-3 mr-1 fill-white" />
                                {language === 'es' ? 'Destacado' : 'Featured'}
                              </Badge>
                            )}
                            <Badge variant="outline">{program.category}</Badge>
                            <div className="flex items-center gap-1">
                              <Input
                                type="number"
                                min="0"
                                defaultValue={program.applicantsOverride || program.applicants || 0}
                                className="w-16 h-7 text-xs text-center px-1 border-gray-300"
                                data-testid={`applicants-input-${program._id}`}
                                onBlur={async (e) => {
                                  const val = parseInt(e.target.value, 10);
                                  if (isNaN(val) || val < 0) return;
                                  try {
                                    await programsAPI.update(program._id, { applicantsOverride: val });
                                    toast({
                                      title: language === 'es' ? 'Postulantes actualizado' : 'Applicants updated',
                                      description: `${val}`
                                    });
                                  } catch (err) {
                                    console.error('Error updating applicants:', err);
                                  }
                                }}
                                onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }}
                              />
                              <span className="text-xs text-gray-500">{language === 'es' ? 'postulantes' : 'applicants'}</span>
                              {program.applicantsReal !== undefined && program.applicantsOverride > 0 && program.applicantsOverride !== program.applicantsReal && (
                                <span className="text-[10px] text-gray-400" title={language === 'es' ? 'Postulaciones reales' : 'Real applications'}>
                                  ({program.applicantsReal} real)
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleToggleFeatured(program._id)}
                            className={`border-2 ${program.featured ? 'border-orange-400 bg-orange-50 text-orange-600' : 'border-gray-200 text-gray-400 hover:text-orange-500 hover:border-orange-300'}`}
                            title={program.featured ? (language === 'es' ? 'Quitar destaque' : 'Remove featured') : (language === 'es' ? 'Destacar' : 'Feature')}
                            data-testid={`toggle-featured-${program._id}`}
                          >
                            <Star className={`w-4 h-4 ${program.featured ? 'fill-orange-400' : ''}`} />
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleEdit(program)}
                            className="border-2"
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleDelete(program._id)}
                            className="border-2 border-red-300 text-red-600 hover:bg-red-50"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                      <p className="text-gray-700 mb-4">{program.description}</p>
                      <div className="grid grid-cols-3 gap-4 text-sm">
                        <div>
                          <span className="text-gray-600">{language === 'es' ? 'Premio:' : 'Prize:'}</span>
                          <span className="ml-2 font-semibold">
                            {program.prizes && program.prizes.length > 0
                              ? `$${program.prizes[0].amount}${program.prizes.length > 1 ? ` (+${program.prizes.length - 1})` : ''}`
                              : program.prize || '-'}
                          </span>
                        </div>
                        <div>
                          <span className="text-gray-600">{language === 'es' ? 'Ubicación:' : 'Location:'}</span>
                          <span className="ml-2 font-semibold">{program.location}</span>
                        </div>
                        <div>
                          <span className="text-gray-600">{language === 'es' ? 'Fecha límite:' : 'Deadline:'}</span>
                          <span className="ml-2 font-semibold">{new Date(program.deadline).toLocaleDateString()}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminPrograms;